<template>
  <auth-screen v-if="authReady && !sessionUser && !snapshotError" @authenticated="onAuthenticated" />
  <div
    v-else-if="snapshotError && !plannerReady"
    class="snapshot-error d-flex flex-column justify-content-center align-items-center"
  >
    <p>{{ snapshotError }}</p>
    <button type="button" class="btn btn-primary" @click="retrySnapshot">重试</button>
  </div>
  <template v-else-if="plannerReady">
  <input class="hidden-input-for-focus" type="text" />
  <div v-show="compatible" id="app-container" class="app-container" :class="{ 'dark-theme': darkTheme }">
    <splash-screen ref="splash"></splash-screen>

    <mobile-shell
      :selected-day="selected_date"
      :is-admin="!!(sessionUser && sessionUser.isAdmin)"
      :app-version="(sessionUser && sessionUser.appVersion) || ''"
      @update:selected-day="selected_date = $event"
    ></mobile-shell>

    <remove-custom-list></remove-custom-list>
    <config-modal @change-columns="weekResetScroll" :configProp="$store.getters.config"></config-modal>
    <clear-data-modal></clear-data-modal>
    <clear-list-modal></clear-list-modal>
    <welcome-modal></welcome-modal>
    <tips-modal></tips-modal>
    <to-do-modal :selectedTodo="selectedTodo"></to-do-modal>
    <active-to-do :activeTodo="activeTodo"> </active-to-do>
    <recurrent-events-modal></recurrent-events-modal>
    <importing-modal :id="'importingModal'" :text="$t('settings.importing')"></importing-modal>
    <importing-modal :id="'exportingModal'" :text="$t('settings.exporting')"></importing-modal>
    <date-range-export-modal></date-range-export-modal>
    <profile-modal
      :user="sessionUser"
      @email-updated="onEmailUpdated"
      @logged-out="onLoggedOut"
    ></profile-modal>
    <reorder-custom-lists-modal @reset-custom-list="resetCustomList"></reorder-custom-lists-modal>

    <div class="position-fixed bottom-0 end-0 p-3" style="z-index: 1056">
      <toast-message id="versionChanges" :text="$t('ui.softwareUpdated')"></toast-message>
      <toast-message id="syncConflict" text="这条已在其他设备先同步，已恢复为服务器版本"></toast-message>
      <toast-message id="syncWriteFailed" :text="syncWriteFailedText"></toast-message>
    </div>
  </div>
  <div v-if="!compatible" class="compatible d-flex flex-column justify-content-center align-items-center p-5">
    <i class="bi-exclamation-diamond mb-4" style="font-size: 100px"></i>
    <h3 style="text-align: center">{{ $t("ui.compatible") }}</h3>
  </div>
  </template>
</template>

<script>
import { defineAsyncComponent } from "vue";
import moment from "moment";
import customToDoListIdsRepository from "./repositories/customToDoListIdsRepository";
import splashScreen from "./components/splashScreen";
import configRepository from "./repositories/configRepository";
import { Modal, Toast } from "bootstrap";
import migrations from "./migrations/migrations";
import notifications from "./helpers/notifications";
import repeatingEventRepository from "./repositories/repeatingEventRepository";
import toDoListRepository from "./repositories/toDoListRepository";
import toastMessage from "./components/toastMessage";
import tasksHelper from "./helpers/tasksHelper";
import authScreen from "./views/authScreen";
const { fetchSession, fetchBootstrap, flush } = require("./sync/engine");
const { loadMeta } = require("./sync/cache");
const { applyBootstrapDocuments, applyWeekToStore, getWeekCache } = require("./sync/sessionSync");
const { loadLocaleMessages } = require("./assets/languages/languages.js");

export default {
  name: "App",
  components: {
    splashScreen,
    toastMessage,
    authScreen,
    removeCustomList: defineAsyncComponent(() => import("./components/comfirmModals/removeCustomList")),
    configModal: defineAsyncComponent(() => import("./views/configModal")),
    welcomeModal: defineAsyncComponent(() => import("./views/welcomeModal")),
    tipsModal: defineAsyncComponent(() => import("./views/tipsModal")),
    toDoModal: defineAsyncComponent(() => import("./views/toDoModal/toDoModal")),
    clearDataModal: defineAsyncComponent(() => import("./components/comfirmModals/clearDataModal.vue")),
    clearListModal: defineAsyncComponent(() => import("./components/comfirmModals/clearListModal.vue")),
    importingModal: defineAsyncComponent(() => import("./views/importingModal.vue")),
    RecurrentEventsModal: defineAsyncComponent(() => import("./views/RecurrentEventsModal.vue")),
    ReorderCustomListsModal: defineAsyncComponent(() => import("./views/ReorderCustomListsModal.vue")),
    activeToDo: defineAsyncComponent(() => import("./components/activeToDo.vue")),
    MobileShell: defineAsyncComponent(() => import("./mobile/MobileShell.vue")),
    DateRangeExportModal: defineAsyncComponent(() => import("./mobile/DateRangeExportModal.vue")),
    ProfileModal: defineAsyncComponent(() => import("./mobile/ProfileModal.vue")),
  },
  data() {
    return {
      selected_date: null,
      cTodoList: this.$store.getters.cTodoListIds,
      calendarHeight: "calc(50% - 50px)",
      initialLoadCompleted: false,
      initialListToLoad: 0,
      initialListLoaded: 0,
      sessionUser: null,
      authReady: false,
      snapshotError: "",
      plannerReady: false,
      syncWriteFailedText: "同步失败",
    };
  },
  created() {
    this.bootstrapSession();
  },
  beforeUnmount() {
    this.stopCloudFlush();
  },
  methods: {
    async bootstrapSession() {
      this.snapshotError = "";
      try {
        this.sessionUser = await fetchSession();
      } catch (error) {
        this.snapshotError = error.message;
        this.authReady = true;
        return;
      }
      this.authReady = true;
      if (!this.sessionUser) return;
      try {
        await this.activateSession();
      } catch (error) {
        this.snapshotError = error.message;
      }
    },
    async onAuthenticated() {
      this.snapshotError = "";
      try {
        this.sessionUser = await fetchSession();
        if (!this.sessionUser) return;
        await this.activateSession();
      } catch (error) {
        this.snapshotError = error.message;
      }
    },
    onEmailUpdated(user) {
      if (!user) return;
      this.sessionUser = { ...(this.sessionUser || {}), id: user.id, email: user.email };
    },
    onLoggedOut() {
      this.stopCloudFlush();
      getWeekCache().clear();
      this.sessionUser = null;
      this.plannerReady = false;
      this.snapshotError = "";
      this.authReady = true;
      this.initialLoadCompleted = false;
      this.initialListToLoad = 0;
      this.initialListLoaded = 0;
      this.$nextTick(() => {
        document.querySelectorAll(".modal-backdrop").forEach((node) => node.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("padding-right");
      });
    },
    async retrySnapshot() {
      if (this.sessionUser) {
        this.snapshotError = "";
        try {
          await this.activateSession();
        } catch (error) {
          this.snapshotError = error.message;
        }
        return;
      }
      await this.bootstrapSession();
    },
    async activateSession() {
      this.snapshotError = "";
      // Drop legacy local sync leftovers (tombstones / stale dirty queue for todos)
      try {
        const raw = localStorage.getItem("syncMeta");
        if (raw) {
          const meta = JSON.parse(raw);
          if (meta && meta.deletedTodos) {
            delete meta.deletedTodos;
            localStorage.setItem("syncMeta", JSON.stringify(meta));
          }
        }
      } catch (_) {
        /* ignore */
      }
      const boot = await fetchBootstrap();
      await applyBootstrapDocuments(boot.documents || []);
      this.loadLocalPlanner();
      const dayId = moment().format("YYYYMMDD");
      const weekStartsOnMonday = this.$store.getters.config.weekStartOnMonday !== false;
      await applyWeekToStore(this.$store, dayId, weekStartsOnMonday);
      this.plannerReady = true;
      await this.$nextTick();
      this.startPlannerUi();
      this.startCloudFlush();
      this.flushCloud();
    },
    loadLocalPlanner() {
      let config = configRepository.load();
      const appVersion = (this.sessionUser && this.sessionUser.appVersion) || "";
      // 本地 schema 迁移均为幂等；版本变化或首次时跑一遍
      if (!appVersion || appVersion != config.version) {
        migrations.migrate();
      }

      if (Notification.permission !== "denied") {
        Notification.requestPermission();
      }
      this.$store.commit("loadCustomTodoListsIds", customToDoListIdsRepository.load());
      this.cTodoList = this.$store.getters.cTodoListIds;
      this.$store.commit("loadConfig", configRepository.load());
      this.applyLocale(this.$store.getters.config.language);

      this.$store.dispatch("loadAllRepeatingEvent").then(
        function () {
          let totalDaysCount = parseInt(this.$store.getters.config.columns) + 2;
          let totalCustomListCount = this.$store.getters.cTodoListIds.length;
          this.initialListToLoad = totalDaysCount + totalCustomListCount;
          this.deleteOldRepeatingEvents();
          this.selected_date = moment().format("YYYYMMDD");
          this.$nextTick(() => {
            this.weekResetScroll();
          });
          this.$store.commit("loadRepeatingEventDateCache", this.$store.getters.repeatingEventList);
        }.bind(this)
      );
    },
    startCloudFlush() {
      this.stopCloudFlush();
      this._onOnline = () => {
        this.flushCloud();
      };
      window.addEventListener("online", this._onOnline);
      this._onPageHide = () => {
        this.flushCloud();
      };
      window.addEventListener("pagehide", this._onPageHide);
      this._onSyncPending = (event) => {
        if (event.detail && event.detail.pending) this.scheduleFlush();
      };
      window.addEventListener("sync-status", this._onSyncPending);
      this._onSyncConflict = (event) => {
        const conflicts = event.detail && event.detail.conflicts;
        if (conflicts && conflicts.length) {
          this.presentSyncConflicts(conflicts);
        }
      };
      window.addEventListener("sync-conflict", this._onSyncConflict);
      this._onSyncWriteFailed = (event) => {
        const message = (event.detail && event.detail.message) || "同步失败";
        this.syncWriteFailedText = message;
        this.$nextTick(() => {
          const el = document.getElementById("syncWriteFailed");
          if (el) new Toast(el).show();
        });
        const listId = event.detail && event.detail.listId;
        if (listId && this.plannerReady) {
          const { reloadTodoListFromServer } = require("./sync/sessionSync.js");
          reloadTodoListFromServer(this.$store, listId).catch(() => {});
        }
      };
      window.addEventListener("sync-write-failed", this._onSyncWriteFailed);
      this._onTodoListServerReplace = (event) => {
        const listId = event.detail && event.detail.listId;
        const payload = event.detail && event.detail.payload;
        if (!listId || !Array.isArray(payload)) return;
        this.$store.commit("loadTodoLists", { todoListId: listId, todoList: payload });
      };
      window.addEventListener("todo-list-server-replace", this._onTodoListServerReplace);
    },
    stopCloudFlush() {
      if (this._onOnline) {
        window.removeEventListener("online", this._onOnline);
        this._onOnline = null;
      }
      if (this._onPageHide) {
        window.removeEventListener("pagehide", this._onPageHide);
        this._onPageHide = null;
      }
      if (this._flushTimer) {
        clearInterval(this._flushTimer);
        this._flushTimer = null;
      }
      if (this._flushSoon) {
        clearTimeout(this._flushSoon);
        this._flushSoon = null;
      }
      if (this._onSyncPending) {
        window.removeEventListener("sync-status", this._onSyncPending);
        this._onSyncPending = null;
      }
      if (this._onSyncConflict) {
        window.removeEventListener("sync-conflict", this._onSyncConflict);
        this._onSyncConflict = null;
      }
      if (this._onSyncWriteFailed) {
        window.removeEventListener("sync-write-failed", this._onSyncWriteFailed);
        this._onSyncWriteFailed = null;
      }
      if (this._onTodoListServerReplace) {
        window.removeEventListener("todo-list-server-replace", this._onTodoListServerReplace);
        this._onTodoListServerReplace = null;
      }
    },
    scheduleFlush() {
      if (this._flushSoon) clearTimeout(this._flushSoon);
      this._flushSoon = setTimeout(() => {
        this.flushCloud();
      }, 1500);
    },
    async flushCloud() {
      if (!this.sessionUser || !this.plannerReady) return;
      if (this._flushing) {
        this._flushAgain = true;
        return;
      }
      this._flushing = true;
      try {
        do {
          this._flushAgain = false;
          const result = await flush(loadMeta(), undefined, { pull: false });
          if (result.conflicts.length) {
            await this.presentSyncConflicts(result.conflicts);
          }
        } while (this._flushAgain);
      } catch (error) {
        // Network failures keep the previous sync meta.
      } finally {
        this._flushing = false;
      }
    },
    async applyLocale(locale) {
      await loadLocaleMessages(this.$i18n, locale || "en");
    },
    async reloadPlannerFromCache() {
      this.$store.commit("loadConfig", configRepository.load());
      await this.applyLocale(this.$store.getters.config.language);
      this.$store.commit("loadCustomTodoListsIds", customToDoListIdsRepository.load());
      this.cTodoList = this.$store.getters.cTodoListIds;
      await this.$store.dispatch("loadAllRepeatingEvent");
      this.$store.commit("loadRepeatingEventDateCache", this.$store.getters.repeatingEventList);
      const dates = this.$store.getters.selectedDates || [];
      await Promise.all(dates.map((date) => this.$store.dispatch("loadTodoLists", date)));
      for (const list of this.cTodoList || []) {
        if (list && list.listId) {
          await this.$store.dispatch("loadTodoLists", list.listId);
        }
      }
    },
    async presentSyncConflicts(conflicts) {
      await this.$nextTick();
      const element = document.getElementById("syncConflict");
      if (element) {
        const toast = new Toast(element);
        toast.show();
      }
      let repeatingEvents = false;
      for (const conflict of conflicts) {
        if (conflict.docType === "todo_list") {
          await this.$store.dispatch("loadTodoLists", conflict.docId);
        } else if (conflict.docType === "config") {
          this.$store.commit("loadConfig", configRepository.load());
          await this.applyLocale(this.$store.getters.config.language);
        } else if (conflict.docType === "custom_list_ids") {
          this.$store.commit("loadCustomTodoListsIds", customToDoListIdsRepository.load());
          this.cTodoList = this.$store.getters.cTodoListIds;
        } else if (conflict.docType === "repeating_event") {
          repeatingEvents = true;
        } else if (conflict.docType === "repeating_event_by_date") {
          await this.$store.dispatch("loadRepeatingEventGeneratedByDate", conflict.docId);
        }
      }
      if (repeatingEvents) {
        await this.$store.dispatch("loadAllRepeatingEvent");
      }
    },
    startPlannerUi() {
      this.calendarHeight = this.$store.getters.config.calendarHeight;
      window.addEventListener("resize", this.weekResetScroll);
      const hideWhenReady = () => {
        setTimeout(this.hideSplash, 200);
      };
      if (document.readyState == "complete") {
        hideWhenReady();
      } else {
        document.onreadystatechange = () => {
          if (document.readyState == "complete") {
            hideWhenReady();
          }
        };
      }

      if (this.$store.getters.config.importing) {
        this.$store.commit("updateConfig", { val: false, key: "importing" });
        configRepository.update(this.$store.getters.config);
      }

      this.resetAppOnDayChange();
    },
    weekMoveLeft: function () {
      this.selected_date = moment(this.selected_date).subtract(1, "d").format("YYYYMMDD");
      this.$refs.weekListContainer.scrollLeft = this.todoListWidth() * 2;
      this.$refs.weekListContainer.scroll({
        left: this.$refs.weekListContainer.scrollLeft - this.todoListWidth(),
        top: 0,
        behavior: "smooth",
      });
    },
    weekMoveRight: function () {
      this.selected_date = moment(this.selected_date).add(1, "d").format("YYYYMMDD");
      this.$refs.weekListContainer.scrollLeft = 0;
      this.$refs.weekListContainer.scroll({
        left: this.$refs.weekListContainer.scrollLeft + this.todoListWidth(),
        top: 0,
        behavior: "smooth",
      });
    },
    deleteOldRepeatingEvents: function () {
      for (const event of Object.entries(this.$store.getters.repeatingEventList)) {
        if (moment(event[1].end_date).isBefore(moment())) {
          repeatingEventRepository.remove(event[0]);
          this.$store.commit("removeRepeatingEvent", event[0]);
        }
      }
    },
    weekResetScroll: function () {
      if (!this.$refs.weekListContainer) return;
      this.$refs.weekListContainer.scrollLeft = this.todoListWidth();
    },
    customMoveRight: function () {
      this.$refs.customListContainer.scrollLeft =
        this.$refs.customListContainer.scrollLeft + this.customTodoListWidth() - 13;
    },
    customMoveLeft: function () {
      this.$refs.customListContainer.scrollLeft = this.$refs.customListContainer.scrollLeft - this.customTodoListWidth();
    },
    resetCustomList: function () {
      this.$nextTick(function () {
        this.$refs.customListContainer.scrollLeft = 0;
      });
    },
    todoListWidth: function () {
      return this.$refs.weekListContainer.clientWidth / this.columns;
    },
    customTodoListWidth: function () {
      return this.$refs.customListContainer.clientWidth / this.customColumns;
    },
    setSelectedDate: function (date) {
      this.selected_date = date;
      this.$nextTick(function () {
        document
          .getElementById("list" + date)
          .getElementsByClassName("new-todo-input")[0]
          .focus();
      });
    },
    hideSplash: function () {
      this.$refs.splash.hideSplash();
      this.checksOnLoadApp();
      if (this.$store.getters.config.firstTimeOpen) {
        this.showWelcomeModal();
      }
    },
    showWelcomeModal: function () {
      let modal = new Modal(document.getElementById("welcomeModal"), {
        backdrop: "static",
      });
      modal.show();
      this.$store.commit("updateConfig", { val: false, key: "firstTimeOpen" });
      configRepository.update(this.$store.getters.config);
    },
    compatible: function () {
      return window.IndexedDB;
    },
    resizerDblClick: function () {
      if (this.$store.getters.config.mainDividerPosition != 1) return;

      this.calendarHeight = "calc(50% - 50px)";
      this.$store.commit("updateConfig", {
        val: this.calendarHeight,
        key: "calendarHeight",
      });
      configRepository.update(this.$store.getters.config);
    },
    resizerMouseDownHandler: function (e) {
      if (this.$store.getters.config.mainDividerPosition != 1) return;

      this.resizerY = e.clientY - 50;
      document.addEventListener("mousemove", this.resizerMouseMoveHandler);
      document.addEventListener("mouseup", this.resizerMouseUpHandler);
    },
    resizerMouseMoveHandler: function (e) {
      this.calendarHeight = `${((e.clientY - 50) * 100) / this.zoom}px`;
    },
    resizerMouseUpHandler: function () {
      document.removeEventListener("mousemove", this.resizerMouseMoveHandler);
      document.removeEventListener("mouseup", this.resizerMouseUpHandler);
      this.$store.commit("updateConfig", {
        val: this.calendarHeight,
        key: "calendarHeight",
      });
      configRepository.update(this.$store.getters.config);
    },
    refreshTodayNotifications: function () {
      notifications.refreshDayNotifications(this, moment().format("YYYYMMDD"));
    },
    todoListMounted: function () {
      this.methodsAfterInitialLoad();
    },
    methodsAfterInitialLoad: function () {
      if (!this.initialLoadCompleted) {
        this.initialListLoaded++;
        if (this.initialListLoaded == this.initialListToLoad) {
          this.initialLoadCompleted = true;
          if (this.$store.getters.config.moveOldTasks) {
            this.moveOldTasksToToday().then(() => {
              this.refreshTodayNotifications();
              this.$store.commit("updateConfig", { val: moment().format("YYYYMMDD"), key: "lastDayOpened" });
              configRepository.update(this.$store.getters.config);
            });
          } else {
            this.refreshTodayNotifications();
            this.$store.commit("updateConfig", { val: moment().format("YYYYMMDD"), key: "lastDayOpened" });
            configRepository.update(this.$store.getters.config);
          }
        }
      }
    },
    resetAppOnDayChange: function () {
      var x = new moment();
      var y = new moment().add(1, "d").startOf("date");
      var duration = moment.duration(y.diff(x)).asMilliseconds();

      setTimeout(
        function () {
          this.refreshTodayNotifications();
          this.resetAppOnDayChange();
        }.bind(this),
        duration
      );
    },
    moveOldTasksToToday: async function () {
      var promise = new Promise((resolve) => {
        var todayListId = moment().format("YYYYMMDD");
        let daysBefore = moment().diff(moment(this.$store.getters.config.lastDayOpened), "days");
        if (daysBefore == 0) daysBefore = 7;
        for (let i = 1; i <= daysBefore; i++) {
          let listId = moment().subtract(i, "d").format("YYYYMMDD");
          this.$store.dispatch("loadTodoLists", listId).then(() => {
            this.$store.commit("moveUndoneItems", { origenId: listId, destinyId: todayListId });
            toDoListRepository.update(listId, this.$store.getters.todoLists[listId]);
            const todayList = this.$store.getters.todoLists[todayListId];
            tasksHelper.applyListOrder(todayList, this.$store.getters.config);
            toDoListRepository.update(todayListId, todayList);
            if (i == daysBefore) {
              resolve("done!");
            }
          });
        }
      });
      return promise;
    },
    setDividerPosition: function (position) {
      this.$nextTick(function () {
        document.getElementById("app-container").classList.add("scrolling");
        setTimeout(() => {
          document.getElementById("app-container").classList.remove("scrolling");
        }, 400);
        this.$store.commit("updateConfig", { val: position, key: "mainDividerPosition" });
        configRepository.update(this.$store.getters.config);
      });
    },
    checkVersion: function () {
      const appVersion = (this.sessionUser && this.sessionUser.appVersion) || "";
      if (!appVersion) return;
      const prev = this.$store.getters.config.version;
      if (appVersion != prev) {
        this.$store.commit("updateConfig", { val: appVersion, key: "version" });
        configRepository.update(this.$store.getters.config);
        // 仅在已有旧版本号时提示升级（避免首次登录空 version 也弹 toast）
        if (prev) {
          var toast = new Toast(document.getElementById("versionChanges"));
          toast.show();
        }
      }
    },
    checksOnLoadApp: function () {
      this.checkVersion();
    },
  },
  computed: {
    dates_array: function () {
      if (!this.selected_date) return [];
      var dates_array = [moment(this.selected_date).subtract(1, "d").format("YYYYMMDD"), this.selected_date];

      for (let i = 1; i < this.columns; i++) {
        dates_array.push(moment(this.selected_date).add(i, "d").format("YYYYMMDD"));
      }

      if (this.$store.getters.config.startCalendarYesterday) {
        dates_array.unshift(moment(this.selected_date).subtract(2, "d").format("YYYYMMDD"));
      } else {
        dates_array.push(moment(this.selected_date).add(this.columns, "d").format("YYYYMMDD"));
      }

      this.$store.commit("updateSelectedDates", dates_array);
      return dates_array;
    },
    showCustomList: function () {
      return this.$store.getters.config.customList;
    },
    showCalendar: function () {
      return this.$store.getters.config.calendar;
    },
    columns: function () {
      return this.$store.getters.config.columns;
    },
    customColumns: function () {
      return this.$store.getters.config.customColumns;
    },
    zoom: function () {
      return this.$store.getters.config.zoom;
    },
    darkTheme: function () {
      return this.$store.getters.config.darkTheme;
    },
    resizableStyle: function () {
      if (this.showCalendar && this.showCustomList) {
        return { height: this.calendarHeight };
      } else {
        return {};
      }
    },
    selectedTodo: function () {
      if (this.$store.getters.actions.selectedTodo) {
        return this.$store.getters.actions.selectedTodo;
      }
      return null;
    },
    activeTodo: function () {
      if (this.$store.getters.activeTodo) {
        return this.$store.getters.activeTodo;
      }
      return null;
    },
    mainDividerPositionClass: function () {
      if (this.$store.getters.config.mainDividerPosition == 0) {
        return "on-bottom";
      } else if (this.$store.getters.config.mainDividerPosition == 1) {
        return "on-center";
      } else {
        return "on-top";
      }
    },
    hideTopListContainer: function () {
      if (!this.$store.getters.config.customList || !this.$store.getters.config.calendar) return false;

      return this.$store.getters.config.mainDividerPosition == 2 ? true : false;
    },
    hideBottomListContainer: function () {
      if (!this.$store.getters.config.customList || !this.$store.getters.config.calendar) return false;

      return this.$store.getters.config.mainDividerPosition == 0 ? true : false;
    },
  },
};
</script>

<style lang="scss">
@import "/src/assets/style/globalVars.scss";

body {
  line-height: unset !important;
}

.todo-lists-container {
  display: flex;
  overflow: auto;
  min-height: 5px;
  height: 5px;
  transition: height 0.15s ease-out 0s;
  margin-top: 20px;
  margin-bottom: 25px;
  // margin-bottom: 5px;
}

.slider-btn {
  padding: 3px;
  font-size: 2rem;
  align-self: center;
  flex-grow: 0;
  margin-left: 6px;
  margin-right: 6px;
  cursor: pointer;
  transition: 0.4s cubic-bezier(0.2, 1, 0.1, 1);
}

.slider-btn:hover {
  border-radius: 6px;
  background-color: #eaecef;
}

.slider-btn:active {
  background-color: #dddfe2;
}

.v3dp__popout {
  border-radius: 7px !important;
}

.side-bar .v3dp__popout {
  margin-left: 72px;
  margin-top: 0px;
}

.todo-slider {
  flex-grow: 1;
  display: flex;
  overflow-x: hidden;
  min-height: -webkit-fill-available;
  min-height: -moz-available;
  height: fit-content;
}

@-moz-document url-prefix() {
  .todo-slider {
    min-height: -moz-available;
    height: unset;
  }
}

.slides {
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
}

.dark-theme *::-webkit-scrollbar-thumb {
  background: #333940;
  border-radius: 5px;
}

.dark-theme *::-webkit-scrollbar-thumb:hover {
  background: #39484f;
}

.dark-theme *::-webkit-scrollbar-thumb:active {
  background: #51656f;
}

.full-screen {
  height: 100%;
  resize: unset;
}

.full-screen .todo-slider {
  margin-top: 20px;
}

/*----------------Dark Theme------------------*/
.dark-theme {
  background-color: #13171d;
  color: #c9d1d9;
}

.dark-theme input {
  background-color: #13171d;
  color: #c9d1d9;
}

.dark-theme input.form-range {
  background-color: unset;
}

.dark-theme .slider-btn:hover {
  border-radius: 6px;
  background-color: #21262d;
}

.dark-theme .slider-btn:active {
  background-color: #2a2e36;
}

.mobile {
  width: 100%;
  height: 100%;
  z-index: 999;
  position: absolute;
  padding: 20%;
}

.dark-theme .mobile {
  background-color: #13171d;
}

.compatible {
  width: 100%;
  height: 100%;
  z-index: 999;
  /*position: absolute;*/
}

.hidden-input-for-focus {
  position: absolute;
  top: -100px;
}

.snapshot-error {
  min-height: 100vh;
  gap: 12px;
}

.main-horizontal-divider {
  z-index: 5;

  &.on-top {
    cursor: unset;

    .inner-main-horizontal-divider {
      display: none;
    }

    .divider-icons-container {
      margin-top: 5px;
      visibility: visible;
      opacity: 0.3;
    }

    .move-to-corner-down,
    .move-to-corner-up,
    .move-to-center-up {
      display: none;
    }
  }

  &.on-bottom {
    cursor: unset;

    .inner-main-horizontal-divider {
      display: none;
    }

    .divider-icons-container {
      margin-top: -25px;
      visibility: visible;
      opacity: 0.3;
    }

    .move-to-corner-up,
    .move-to-corner-down,
    .move-to-center-down {
      display: none;
    }
  }

  &.on-center {
    .move-to-center-down,
    .move-to-center-up {
      display: none;
    }
  }

  &:hover {
    .divider-icons-container {
      visibility: visible;
      opacity: 1;
    }
  }
}

.divider-icons-container {
  visibility: hidden;
  opacity: 0;
  transition: 0.4s cubic-bezier(0.2, 1, 0.1, 1);
  z-index: 6;
  position: absolute;
  right: 70px;
  margin-top: -8px;
}

.divider-icons {
  @include btn-icon;
  padding: 6px;
  background-color: white;

  &:hover {
    opacity: 1;
  }

  .dark-theme & {
    background-color: #13171d;
  }
}

.hidden-lists-container {
  height: 0px !important;
  margin: 0px;
  min-height: 0px;
}

.full-screen-divider {
  height: 100% !important;
}
</style>
